import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { GenerateCategoriesDialog } from '../lines-of-business/GenerateCategoriesDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { useTenant } from '../../core/contexts/TenantContext';
import { useLineOfBiz } from '../../core/contexts/LineOfBizContext';
import type { Category, LineOfBizCategory } from '../../core/types/data-models';

/** Form payload shape — only the user-supplied fields, not the calculated aggregates. */
interface CategoryFormValues {
  id?: number;
  name: string;
  description: string | null;
  subcategories: string | null;
  commonCategoryId: number | null;
  promptsWeight: number;
}

const EMPTY_FORM: CategoryFormValues = {
  name: '',
  description: null,
  subcategories: null,
  commonCategoryId: null,
  promptsWeight: 0,
};

/**
 * Percentage of the LOB's prompts that fall under one category. Returns
 * a whole number; guards against a zero denominator (no prompts yet).
 */
function promptShare(count: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((count / total) * 100);
}

/**
 * Categories page rendered as a list of expandable cards. Each card shows
 * the category name as the title; expanding reveals the description, the
 * linked global category, and aggregate usage counts. The toolbar carries
 * the create + expand-all / collapse-all controls; row-level edit and
 * delete affordances live on the card header.
 *
 * Designed for the <40-row case the user described — no pagination, no
 * server-side search. If the list grows much past that we'll need to
 * revisit (filter / virtualize).
 */
export function LineOfBizCategoriesPage() {
  const { activeTenantId } = useTenant();
  const { activeLineOfBizId } = useLineOfBiz();
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['line-of-biz-categories', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources.lineOfBizCategories.list({
        pageSize: 200,
        sort: 'name',
        filter: { lineOfBizId: activeLineOfBizId as number },
      }),
    enabled: activeLineOfBizId != null,
  });

  // Total number of prompts in this LOB — the denominator for each
  // category's "share of prompts" figure. We only need the count, so we
  // ask for a single row and read `page.total` off the envelope.
  const promptsTotalQuery = useQuery({
    queryKey: ['prompts', 'count', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources.prompts.list({
        page: 1,
        pageSize: 1,
        filter: { lineOfBizId: activeLineOfBizId as number },
      }),
    enabled: activeLineOfBizId != null,
  });
  const totalLobPrompts = promptsTotalQuery.data?.page.total ?? 0;

  // Shared catalog of common categories — used both for the card's
  // "Common category" line and for the form's picker.
  const commonCatalog = useQuery({
    queryKey: ['categories', 'select'],
    queryFn: () => resources.categories.list({ pageSize: 500, sort: 'name' }),
  });
  const commonNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of commonCatalog.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [commonCatalog.data]);

  const rows: LineOfBizCategory[] = list.data?.data ?? [];

  // Running total of the per-category prompt weights. All categories under
  // a LOB are meant to sum to 100% — surface the current total so the user
  // can tell at a glance whether the budget is balanced.
  const totalWeight = useMemo(
    () => rows.reduce((sum, r) => sum + (r.promptsWeight ?? 0), 0),
    [rows],
  );

  // Expanded-state lives in a Set so we can flip an entire group at once
  // (expand-all / collapse-all) without re-rendering every card. Auto-
  // sync to the row list — when categories are added / removed the set
  // should stay focused on existing ids only.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set<number>();
      for (const r of rows) if (prev.has(r.id)) next.add(r.id);
      return next;
    });
  }, [rows]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(rows.map((r) => r.id)));
  const collapseAll = () => setExpanded(new Set());
  const allExpanded = rows.length > 0 && expanded.size === rows.length;

  // Page-level mode toggle — mirrors the OrganizationsPage convention.
  // Defaults to `'view'` so casual browsing can't accidentally edit; in
  // view mode the toolbar's New / Generate buttons and the per-row
  // edit/delete icons are hidden.
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  // ----- Create / edit dialog -----------------------------------------
  // Single dialog used for both flows; `editing.id` is the discriminator.
  // `subcategoriesMode` toggles the Subcategories field between the raw
  // textarea ('edit') and a rendered Markdown preview ('view'); reset
  // to 'edit' on every open so the user always lands in the editor.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryFormValues>(EMPTY_FORM);
  const [subcategoriesMode, setSubcategoriesMode] =
    useState<'edit' | 'view'>('view');
  // Subcategories section starts collapsed so the (often long) Markdown
  // doesn't dominate the dialog; the user expands it to read/edit.
  const [subcategoriesExpanded, setSubcategoriesExpanded] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(EMPTY_FORM);
    // New category has nothing to preview — drop the user straight into
    // the editor and expand the section so it's ready to type into.
    setSubcategoriesMode('edit');
    setSubcategoriesExpanded(true);
    setFormError(null);
    setDialogOpen(true);
  };
  const openEdit = (row: LineOfBizCategory) => {
    setEditing({
      id: row.id,
      name: row.name,
      description: row.description ?? null,
      subcategories: row.subcategories ?? null,
      commonCategoryId: row.commonCategoryId,
      promptsWeight: row.promptsWeight ?? 0,
    });
    // Existing category — default to the rendered (VIEW) preview and keep
    // the section collapsed; the user expands it to read the subcategories.
    setSubcategoriesMode('view');
    setSubcategoriesExpanded(false);
    setFormError(null);
    setDialogOpen(true);
  };

  const create = useMutation({
    mutationFn: (data: CategoryFormValues) =>
      resources.lineOfBizCategories.create({
        name: data.name,
        description: data.description,
        subcategories: data.subcategories,
        commonCategoryId: data.commonCategoryId,
        promptsWeight: data.promptsWeight,
        lineOfBizId: activeLineOfBizId as number,
        ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
      } as Partial<LineOfBizCategory>),
    onSuccess: () => {
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-categories'] });
    },
    onError: (err) => setFormError(extractErrorMessage(err)),
  });

  const update = useMutation({
    mutationFn: (data: CategoryFormValues & { id: number }) =>
      resources.lineOfBizCategories.update(data.id, {
        name: data.name,
        description: data.description,
        subcategories: data.subcategories,
        commonCategoryId: data.commonCategoryId,
        promptsWeight: data.promptsWeight,
      } as Partial<LineOfBizCategory>),
    onSuccess: () => {
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-categories'] });
    },
    onError: (err) => setFormError(extractErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => resources.lineOfBizCategories.remove(id),
    onSuccess: () => {
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-categories'] });
    },
  });

  // Two distinct delete states:
  //   - `blockedDelete` → the user clicked Delete on a category that is
  //     still referenced by prompts/sets. We show a read-only info
  //     dialog explaining why deletion is refused.
  //   - `confirmDelete` → category isn't in use; we ask the user to
  //     confirm before actually firing the DELETE.
  // Keeping them separate makes the dialog markup straightforward and
  // avoids a single dialog that toggles between two modes.
  const [confirmDelete, setConfirmDelete] = useState<LineOfBizCategory | null>(null);
  const [blockedDelete, setBlockedDelete] = useState<LineOfBizCategory | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  /**
   * Decide whether to block delete or open the confirm dialog. The two
   * `*Count` aggregates on the row already tell us how many ref-rows
   * exist; we treat any non-zero total as "in use".
   */
  const requestDelete = (row: LineOfBizCategory) => {
    const inUse =
      (row.reportTemplatePromptCount ?? 0) + (row.reportTemplatePromptSetCount ?? 0);
    if (inUse > 0) {
      setBlockedDelete(row);
    } else {
      setConfirmDelete(row);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!editing.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (editing.id != null) {
      await update.mutateAsync({ ...editing, id: editing.id });
    } else {
      await create.mutateAsync(editing);
    }
  };

  if (activeLineOfBizId == null) {
    return (
      <Box>
        <PageHeader
          title="Categories"
          description="Categories scoped to a specific line of business; optionally derived from a global catalog entry."
        />
        <Alert severity="info">
          Select a line of business from the sidebar to view its categories.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Categories"
        description="Categories scoped to a specific line of business; optionally derived from a global catalog entry."
        actions={
          // Toolbar — expand/collapse-all on the left, then (only in
          // edit mode) the Generate + New buttons, then the page-level
          // View/Edit toggle on the right. View mode hides destructive
          // and create affordances so casual browsing can't accidentally
          // mutate; mirrors the OrganizationsPage convention.
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
              <span>
                <IconButton
                  onClick={allExpanded ? collapseAll : expandAll}
                  disabled={rows.length === 0}
                  aria-label={allExpanded ? 'collapse all' : 'expand all'}
                >
                  {allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                </IconButton>
              </span>
            </Tooltip>
            {mode === 'edit' && (
              <>
                {/* Opens the GenerateCategoriesDialog — kicks off the
                    LLM job, shows progress, then lets the user pick
                    which suggested categories (with their subcategory
                    Markdown) to write into this LOB. */}
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setGenerateOpen(true)}
                >
                  Generate categories
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                >
                  New
                </Button>
              </>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, next) => {
                if (next === 'view' || next === 'edit') setMode(next);
              }}
              aria-label="page mode"
            >
              <ToggleButton value="view" aria-label="view mode">
                <Tooltip title="View mode">
                  <VisibilityIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="edit" aria-label="edit mode">
                <Tooltip title="Edit mode">
                  <EditNoteIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      />

      {list.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractErrorMessage(list.error)}
        </Alert>
      )}

      {/* Prompt-weight budget banner — green when the weights sum to
          exactly 100%, otherwise a warning showing the running total so
          the user knows how far off balance the categories are. */}
      {rows.length > 0 && (
        <Alert severity={totalWeight === 100 ? 'success' : 'warning'} sx={{ mb: 2 }}>
          Prompt weights total <strong>{totalWeight}%</strong>
          {totalWeight === 100
            ? ' — categories are balanced.'
            : ` — should sum to 100% (off by ${Math.abs(100 - totalWeight)}%).`}
        </Alert>
      )}

      {!list.isLoading && rows.length === 0 ? (
        <Alert severity="info">
          No categories yet. Click <strong>New</strong> to add one.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {rows.map((row) => {
            const isOpen = expanded.has(row.id);
            const commonName =
              row.commonCategoryId == null
                ? null
                : commonNameById.get(row.commonCategoryId) ?? `#${row.commonCategoryId}`;
            return (
              <Accordion
                key={row.id}
                expanded={isOpen}
                onChange={() => toggle(row.id)}
                disableGutters
                // Square card edge so the list reads as a stack of tiles;
                // the default rounded paper is too noisy on a long list.
                // Hover affordance lives on the summary row only so the
                // expanded body doesn't tint while you're reading it.
                sx={{
                  '&:before': { display: 'none' },
                  borderRadius: 1,
                  transition: (t) => t.transitions.create(['box-shadow', 'border-color']),
                  '&:hover': {
                    boxShadow: 2,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ flexGrow: 1, pr: 1 }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: 'primary.main' }}
                    >
                      {row.name}
                    </Typography>
                    {/* Row id — only useful in edit mode (when the user
                        might cross-reference it with a DB record). In view
                        mode an empty spacer keeps the prompts chip and
                        action buttons pinned to the right. */}
                    {mode === 'edit' ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexGrow: 1, fontFamily: 'monospace' }}
                      >
                        #{row.id}
                      </Typography>
                    ) : (
                      <Box sx={{ flexGrow: 1 }} />
                    )}
                    <Tooltip title="Configured weight — target share of this LOB's prompt budget assigned to this category">
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={`Weight ${row.promptsWeight ?? 0}%`}
                      />
                    </Tooltip>
                    <Tooltip title="Actual share — this category's prompts as a percentage of the LOB's total prompts">
                      <Chip
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label={`${promptShare(row.promptCount, totalLobPrompts)}% of prompts`}
                      />
                    </Tooltip>
                    <Tooltip title="Prompts whose default category is this one">
                      <Chip
                        size="small"
                        label={`${row.promptCount} prompt${row.promptCount === 1 ? '' : 's'}`}
                      />
                    </Tooltip>
                    {/* Row actions — only visible in edit mode. Wrapped
                        in click-stoppers so opening the edit dialog
                        doesn't also toggle the accordion. */}
                    {mode === 'edit' && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(row);
                            }}
                            aria-label="edit category"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={remove.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDelete(row);
                            }}
                            aria-label="delete category"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body2">
                        {row.description || <em style={{ opacity: 0.6 }}>No description</em>}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Subcategories
                      </Typography>
                      {/* Render the stored Markdown. Tight overrides on
                          list / paragraph spacing match the dialog
                          preview so edit and view modes look the same. */}
                      {row.subcategories ? (
                        <Box
                          sx={{
                            '& ul, & ol': { my: 0.5, pl: 3 },
                            '& li': { mb: 0.25 },
                            '& p': { my: 0.5 },
                            '& h1, & h2, & h3': {
                              fontSize: '0.95rem',
                              fontWeight: 700,
                              mt: 1,
                              mb: 0.5,
                              '&:first-of-type': { mt: 0 },
                            },
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                          }}
                        >
                          <ReactMarkdown>{row.subcategories}</ReactMarkdown>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          <em style={{ opacity: 0.6 }}>None</em>
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Common category
                      </Typography>
                      <Typography variant="body2">
                        {commonName ?? <em style={{ opacity: 0.6 }}>None</em>}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Line-of-business prompts using this category
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {row.promptCount} of {totalLobPrompts} (
                        {promptShare(row.promptCount, totalLobPrompts)}%)
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      <FormDialog
        open={dialogOpen}
        title={editing.id != null ? 'Edit category' : 'New category'}
        submitLabel={editing.id != null ? 'Save' : 'Create'}
        loading={create.isPending || update.isPending}
        onClose={() => {
          if (create.isPending || update.isPending) return;
          setDialogOpen(false);
        }}
        onSubmit={handleSubmit}
      >
        {formError && <Alert severity="error">{formError}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Name"
            required
            value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
          <TextField
            label="Description"
            multiline
            minRows={2}
            value={editing.description ?? ''}
            onChange={(e) =>
              setEditing({
                ...editing,
                description: e.target.value === '' ? null : e.target.value,
              })
            }
          />
          {/* Subcategories — free-form Markdown in a collapsible section.
              Collapsed by default to keep the dialog compact; expand to
              read (VIEW) the rendered Markdown or switch to EDIT to change
              the raw text. */}
          <Accordion
            disableGutters
            elevation={0}
            expanded={subcategoriesExpanded}
            onChange={(_, exp) => setSubcategoriesExpanded(exp)}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">
                Subcategories (Markdown)
                {!subcategoriesExpanded && editing.subcategories?.trim() ? (
                  <Box component="span" sx={{ ml: 1, fontStyle: 'italic', opacity: 0.7 }}>
                    — expand to view
                  </Box>
                ) : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Edit/View toggle — flip between the raw textarea and the
                  rendered Markdown preview without leaving the dialog. */}
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={subcategoriesMode}
                  onChange={(_, next) => {
                    if (next) setSubcategoriesMode(next);
                  }}
                >
                  <ToggleButton value="edit">Edit</ToggleButton>
                  <ToggleButton value="view">View</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {subcategoriesMode === 'edit' ? (
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={editing.subcategories ?? ''}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    subcategories: e.target.value === '' ? null : e.target.value,
                  })
                }
                placeholder={'- Subcategory name — short description'}
                helperText="Optional — Markdown describing the subcategories under this category."
                InputProps={{
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: 13,
                    lineHeight: 1.5,
                  },
                }}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minHeight: 120,
                  maxHeight: '40vh',
                  overflow: 'auto',
                  backgroundColor: 'background.default',
                  '& ul, & ol': { my: 0.5, pl: 3 },
                  '& li': { mb: 0.25 },
                  '& p': { my: 0.5 },
                  '& h1, & h2, & h3': {
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    mt: 1.5,
                    mb: 0.5,
                    '&:first-of-type': { mt: 0 },
                  },
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                }}
              >
                {editing.subcategories?.trim() ? (
                  <ReactMarkdown>{editing.subcategories}</ReactMarkdown>
                ) : (
                  <Box sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    Nothing to preview yet.
                  </Box>
                )}
              </Paper>
            )}
            </AccordionDetails>
          </Accordion>
          <EntitySelect<Category>
            cacheKey={['categories', 'select']}
            service={resources.categories}
            label="Common category"
            helperText="Optional — link this LOB category to a global catalog entry."
            value={editing.commonCategoryId}
            onChange={(id) => setEditing({ ...editing, commonCategoryId: id })}
          />
          {/* Prompt weight — percentage share of this LOB's prompt budget.
              All categories should sum to 100%; we don't enforce it here,
              the page banner surfaces the running total instead. */}
          <TextField
            label="Prompts weight (%)"
            type="number"
            value={editing.promptsWeight}
            onChange={(e) => {
              const n = Number(e.target.value);
              setEditing({
                ...editing,
                promptsWeight: Number.isFinite(n) ? n : 0,
              });
            }}
            inputProps={{ min: 0, max: 100, step: 1 }}
            helperText="Share of the LOB's total prompts. All categories should sum to 100%."
          />
        </Stack>
      </FormDialog>

      <GenerateCategoriesDialog
        open={generateOpen}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setGenerateOpen(false)}
      />

      {/* Delete-blocked: category is still referenced by prompts and/or
          prompt-sets in this LOB's report templates. The Confirm button
          is a no-op "OK" — there's nothing to confirm, the dialog is
          informational. */}
      <ConfirmDialog
        open={blockedDelete != null}
        title="Can't delete category"
        message={
          blockedDelete
            ? `"${blockedDelete.name}" is used by ${
                (blockedDelete.reportTemplatePromptCount ?? 0)
                  + (blockedDelete.reportTemplatePromptSetCount ?? 0)
              } report-template prompt${
                ((blockedDelete.reportTemplatePromptCount ?? 0)
                  + (blockedDelete.reportTemplatePromptSetCount ?? 0)) === 1
                  ? ''
                  : 's'
              } / prompt sets. Reassign or remove those first, then try again.`
            : ''
        }
        confirmLabel="OK"
        cancelLabel="Close"
        onConfirm={() => setBlockedDelete(null)}
        onClose={() => setBlockedDelete(null)}
      />

      <ConfirmDialog
        open={confirmDelete != null}
        title="Delete category?"
        message={
          confirmDelete
            ? `Delete "${confirmDelete.name}"? This can't be undone.`
            : ''
        }
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
        }}
        onClose={() => {
          if (remove.isPending) return;
          setConfirmDelete(null);
        }}
      />
    </Box>
  );
}
